import * as React from 'react';
import * as styles from './attribute-row.module.scss';
import { SegmentAttributeGroup, SegmentAttributeOperator } from '../../../models/segments';
import Attribute from './attribute';
import AttributeMenu from './attribute-menu';
import useAttributes from '../../../hooks/useSegmentAttributes';
import OperatorSelect from './operator-select';
import { useBuildSegment } from './useBuildSegment';
import clsx from 'clsx';
import { findBaseAttributeId } from '../../../utilities/attribute-filter';

interface Props {
  attributeGroup: SegmentAttributeGroup;
  /** see explanation of position in the useBuildSegment reducer function */
  position: number[];
  exclude?: boolean;
}

const AttributeGroup = ({ attributeGroup, position, exclude }: Props) => {
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const { dispatch } = useBuildSegment(exclude);
  const attributesQuery = useAttributes();

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleApply = (checked: number[]) => {
    if (checked.length > 0) {
      dispatch({ type: 'applyAttributes', checked, attributes: attributesQuery.data.attributes, position });
    }
    setDialogOpen(false);
  };

  const handleOpen = () => {
    setDialogOpen(true);
  };

  const handleDelete = (attributeId: number, attributeIndex: number) => {
    dispatch({ type: 'deleteAttribute', attributeId: attributeId, position: position, attributeIndex: attributeIndex });
  };

  return (
    <div
      className={clsx(
        styles.attributeGroup,
        attributeGroup.predicates.length > 0 && position.length > 1 && styles.hasGroupBracket
      )}
    >
      {attributeGroup.predicates.length === 0 && (
        <Attribute
          selectedAttribute={undefined}
          required={false}
          handleDelete={handleDelete}
          handleOpen={handleOpen}
          handleAddToGroup={handleOpen}
          position={position}
          exclude={exclude}
        />
      )}
      {attributeGroup.predicates.map((predicate, idx) => (
        <React.Fragment key={`${findBaseAttributeId(predicate)}-${idx}`}>
          {idx === 1 && (
            <div className={styles.attributeOperator}>
              <OperatorSelect
                operator={attributeGroup.operator}
                handleChange={(operator) => {
                  dispatch({ type: 'updateOperator', operator, position });
                }}
              />
            </div>
          )}
          {idx > 1 && (
            <div className={styles.attributeOperator}>
              <div className={styles.attributeOperatorText}>
                <p className={styles.groupOperatorText}>{SegmentAttributeOperator[attributeGroup.operator]}</p>
              </div>
            </div>
          )}
          {'attributeId' in predicate ? (
            <Attribute
              key={`attribute-${findBaseAttributeId(predicate)}`}
              selectedAttribute={predicate}
              required={false}
              handleDelete={handleDelete}
              handleOpen={handleOpen}
              handleAddToGroup={handleOpen}
              position={position}
              exclude={exclude}
              attributeIndex={idx}
            />
          ) : (
            <AttributeGroup attributeGroup={predicate} position={[...position, idx]} exclude={exclude} />
          )}
        </React.Fragment>
      ))}
      <AttributeMenu open={dialogOpen} onCancel={handleCancel} onApply={handleApply} />
    </div>
  );
};

export default AttributeGroup;
